import * as React from 'react';
import {
    AddedArticleIcon,
    addIcon,
    alertErrorIcon,
    alertSuccessIcon,
    discountIcon,
    FaOptionIcon,
    hiddenArticleIcon,
    NamedIconsType,
    openIcon,
} from '@europrocurement/l2d-icons';
import { Box, Card, PaletteColor, Typography, useTheme } from '@mui/material';
import { SortableItem, SortableList } from '@europrocurement/l2d-modules/modules/Offres';
import { DossierTarifDTOJsonldDonneesFacturationRead } from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import {
    Accordion,
    FlexyButton,
    ListCardBone,
    SkeletonLoader,
    Switch,
    useModal,
} from '@europrocurement/flexy-components';
import {
    ADD_ARTICLE_DATA_PREFACTURATION_CTX,
    ArticlePrefacturation as ArticlePrefacturationType,
    REMOVE_ARTICLE_DATA_PREFACTURATION_CTX,
    UPDATE_ARTICLE_DATA_PREFACTURATION_CTX,
    useCollectionBillingCtx,
    useCollectionBillingCtxDispatch,
    type Collection,
} from '@europrocurement/l2d-modules/providers/CollectionBillingProvider';
import { Link } from 'react-router-dom';
import {
    detailDossierPath,
    dossiersBasePath,
} from '@europrocurement/l2d-modules/modules/Dossiers/constants';
import ArticlePrefacturationHeader from '../../../ArticlePrefacturationHeader/ArticlePrefacturationHeader';
import Article from '../../../Article/Article';
import NoResult from '../../../NoResult/NoResult';
import ArticleAdd from '../../../ArticleAdd/ArticleAdd';
import useDossierFacturationActions from '../../hooks/useDossierFacturationActions';
import OrderDetailsTotalAmount from './OrderDetailsTotalAmounts';

type OrderDetailsSectionProps = {
    collections: Collection[];
    onChangeDossiersSelected?: (dossierIds: number[], id: number, checked: boolean) => void;
};

const OrderDetailsSection: React.FunctionComponent<OrderDetailsSectionProps> = function ({
    collections,
    onChangeDossiersSelected,
}: OrderDetailsSectionProps) {
    const { modalActions } = useModal();
    const { palette } = useTheme();

    const [remisesInvisiblesAffichees, setRemisesInvisiblesAffichees] =
        React.useState<boolean>(false);

    const {
        getDataPrefacturation,
        getBillingDossierIds,
        isDataPrefacturationLoaded,
        isBillingDossierIdsLoaded,
    } = useCollectionBillingCtx();
    const dataPrefacturation = getDataPrefacturation();
    const dossiersSelected = getBillingDossierIds();
    const billingDispatch = useCollectionBillingCtxDispatch();

    const { onUpdateArticles, sortableListOnChange } = useDossierFacturationActions();
    const dataLoaded = !isDataPrefacturationLoaded() || !isBillingDossierIdsLoaded();

    if (dataLoaded) {
        return null;
    }

    const dataPrefacturationDossiers = dataPrefacturation?.dossiers || [];

    const refreshArticles = async () => {
        const dossierIds = collections.map((c) => c.dossierIds).flat();

        if (!dossierIds.length) {
            return;
        }

        try {
            await onUpdateArticles(dossierIds);
        } catch (e) {
            console.error(e);
        }
    };

    const getArticleIcon = (article: ArticlePrefacturationType): NamedIconsType | undefined => {
        if (remisesInvisiblesAffichees && article.fAjouteCalcul) {
            return AddedArticleIcon;
        }
        if (!article.articleFacturable && !article.articleFacture) {
            return alertErrorIcon;
        }
        return undefined;
    };

    const afterSubmit = async () => {
        modalActions.close();

        await refreshArticles();
    };

    const handleUpdateArticle = (article: ArticlePrefacturationType) => {
        if (billingDispatch) {
            billingDispatch({
                type: UPDATE_ARTICLE_DATA_PREFACTURATION_CTX,
                payload: article,
            });
        }
    };

    const handleCreateArticle = (dossierId: number, article: ArticlePrefacturationType) => {
        if (billingDispatch) {
            billingDispatch({
                type: ADD_ARTICLE_DATA_PREFACTURATION_CTX,
                payload: { dossierId, article },
            });
        }
    };

    const handleDeleteArticle = async (articleId: number) => {
        if (billingDispatch) {
            billingDispatch({
                type: REMOVE_ARTICLE_DATA_PREFACTURATION_CTX,
                payload: articleId,
            });
        }
    };

    const handleSortableListOnChange = async (
        items: Array<ArticlePrefacturationType>,
        dossier: DossierTarifDTOJsonldDonneesFacturationRead,
        dossierIdx: number,
    ) => {
        if (!dataPrefacturation) {
            return;
        }

        await sortableListOnChange(dataPrefacturation, items, dossier, dossierIdx);
        await refreshArticles();
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                boxShadow: 'none',
                padding: 0,
                mx: 0,
            }}
            id="DetailsDeLaCommande"
        >
            <Accordion
                sx={{
                    boxShadow: 'none',
                }}
                title="Détails de la commande"
                titlesSx={{
                    fontWeight: '600',
                    boxShadow: 'none',
                }}
                headerSx={{ boxShadow: 'none' }}
                expanded
                expandColor={palette.text.primary as unknown as PaletteColor}
                expandLabel={
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            <Typography
                                variant="l2dh7"
                                sx={{ marginRight: '10px', fontSize: '0.875rem' }}
                            >
                                Afficher articles cachés
                            </Typography>
                            <Switch
                                value={remisesInvisiblesAffichees}
                                checked={remisesInvisiblesAffichees}
                                onClick={(e) => {
                                    e.stopPropagation(); // Évite de déclencher l'ouverture ou la fermeture de l'accordéon
                                }}
                                onChange={(e, value) => {
                                    setRemisesInvisiblesAffichees(value);
                                }}
                            />
                        </Box>
                    </Box>
                }
            >
                <SkeletonLoader
                    isLoading={!(collections && dataPrefacturationDossiers)}
                    skeleton={<ListCardBone />}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            boxShadow: 'none',
                            padding: 0,
                        }}
                    >
                        {collections &&
                            dataPrefacturationDossiers.map((dossier, dossierIdx) => (
                                <Box key={dossier.id}>
                                    <Accordion
                                        sx={{
                                            boxShadow: 'none',
                                        }}
                                        title={
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '5px',
                                                }}
                                            >
                                                {dossier.prestations
                                                    ?.map((prestation) => prestation.libelle)
                                                    .join(' - ') || ''}
                                                {!dossier.dossierFacturable &&
                                                !dossier.dossierFacture ? (
                                                    <FaOptionIcon
                                                        {...alertErrorIcon.props}
                                                        tooltip={dossier.motifNonFacturable}
                                                    />
                                                ) : null}

                                                {dossier.dossierFacture ? (
                                                    <FaOptionIcon
                                                        {...alertSuccessIcon.props}
                                                        tooltip="Dossier facturé"
                                                    />
                                                ) : null}

                                                <Link
                                                    to={`/${dossiersBasePath}/${detailDossierPath.replace(':idDossier', dossier.id as unknown as string)}`}
                                                    target="_blank"
                                                    style={{
                                                        textDecoration: 'none',
                                                        color: palette.grey[400],
                                                    }}
                                                >
                                                    <FaOptionIcon
                                                        {...openIcon.props}
                                                        size="sm"
                                                    />
                                                </Link>
                                            </Box>
                                        }
                                        titlesSx={{
                                            fontWeight: '600',
                                        }}
                                        subtitle={`Ref. Dossier #${dossier.numero}`}
                                        expanded
                                        expandColor={
                                            palette.text.primary as unknown as PaletteColor
                                        }
                                        expandLabel={
                                            <OrderDetailsTotalAmount
                                                totalAmount={dossier.ttc as string}
                                                totalRemainingAmount={dossier.resteAFacturer}
                                            />
                                        }
                                        footerActions={
                                            dossier.prestations && [
                                                <FlexyButton
                                                    variant="outlined"
                                                    color={palette.primary.dark}
                                                    onClick={() => {
                                                        if (!dossier.prestations) {
                                                            throw new Error(
                                                                'dossier.prestations is undefined',
                                                            );
                                                        }
                                                        if (!dataPrefacturation?.tauxTvas) {
                                                            throw new Error(
                                                                'dataPrefacturation.tauxTvas is undefined',
                                                            );
                                                        }

                                                        modalActions.call(
                                                            <ArticleAdd
                                                                tauxTva={
                                                                    dataPrefacturation.tauxTvas
                                                                }
                                                                prestations={dossier.prestations}
                                                                afterSubmit={afterSubmit}
                                                                onCreate={(article) =>
                                                                    handleCreateArticle(
                                                                        dossier.id,
                                                                        article,
                                                                    )
                                                                }
                                                            />,
                                                        );
                                                    }}
                                                    icon={addIcon}
                                                    label="Ajouter un article"
                                                    disabled={
                                                        dossier.articles?.some(
                                                            (article) =>
                                                                article.articleFacture &&
                                                                !article.articleFactureValidee,
                                                        ) ?? false
                                                    }
                                                />,
                                            ]
                                        }
                                        tooltipDisabled={dossier.motifNonFacturable}
                                        tooltipSelected="Déselectionner ce dossier pour la facturation"
                                        tooltipSelect="Sélectionner ce dossier pour la facturation"
                                        checkBox={(dataPrefacturation?.dossiers?.length ?? 0) > 1}
                                        checkBoxDisabled={!dossier.dossierFacturable}
                                        checked={
                                            dossier.id !== undefined &&
                                            dossiersSelected &&
                                            dossiersSelected.includes(dossier.id)
                                        }
                                        onChange={(checked) => {
                                            if (
                                                dossier.id !== undefined &&
                                                onChangeDossiersSelected
                                            ) {
                                                onChangeDossiersSelected(
                                                    collections.map((c) => c.dossierIds).flat(),
                                                    dossier.id,
                                                    checked,
                                                );
                                            }
                                        }}
                                    >
                                        <ArticlePrefacturationHeader />

                                        {dossier.articles ? (
                                            <SortableList
                                                style={{ margin: '0px' }}
                                                items={dossier.articles
                                                    .filter(
                                                        (art) =>
                                                            art.id !== undefined &&
                                                            !art.fMasqueCalcul,
                                                    )
                                                    .sort((a, b) => a.rang - b.rang)}
                                                onChange={(items) => {
                                                    handleSortableListOnChange(
                                                        items,
                                                        dossier,
                                                        dossierIdx,
                                                    );
                                                }}
                                                renderItem={(article) => {
                                                    const key = `facturation_collection_article_${article.id}`;
                                                    const articleIcon = getArticleIcon(article);
                                                    return (
                                                        <SortableItem
                                                            id={article.id}
                                                            key={key}
                                                        >
                                                            <Article
                                                                article={article}
                                                                onUpdate={handleUpdateArticle}
                                                                onDelete={handleDeleteArticle}
                                                                afterSubmit={afterSubmit}
                                                                sortabled
                                                                icon={articleIcon}
                                                                iconTooltip={
                                                                    article.motifNonFacturable
                                                                }
                                                            />
                                                        </SortableItem>
                                                    );
                                                }}
                                            />
                                        ) : (
                                            <NoResult
                                                title="Aucun article pour le moment"
                                                paddingSx="15px"
                                            />
                                        )}
                                        {dossier.articles && remisesInvisiblesAffichees
                                            ? dossier.articles
                                                  .filter(
                                                      (art) =>
                                                          art.id !== undefined && art.fMasqueCalcul,
                                                  )
                                                  .map((article) => (
                                                      <Article
                                                          article={article}
                                                          sx={{ marginTop: '10px' }}
                                                          icon={hiddenArticleIcon}
                                                      />
                                                  ))
                                            : null}
                                        {dossier.reductions && remisesInvisiblesAffichees
                                            ? dossier.reductions.map((reduction) => {
                                                  const articleReduction: ArticlePrefacturationType =
                                                      {
                                                          ...reduction,
                                                          id: Math.random(),
                                                          idAnnonce:
                                                              reduction.idAnnonce !== undefined
                                                                  ? String(reduction.idAnnonce)
                                                                  : undefined,
                                                      };
                                                  return (
                                                      <Article
                                                          article={articleReduction}
                                                          sx={{ marginTop: '10px' }}
                                                          icon={discountIcon}
                                                      />
                                                  );
                                              })
                                            : null}
                                    </Accordion>
                                </Box>
                            ))}
                    </Box>
                </SkeletonLoader>
            </Accordion>
        </Card>
    );
};

export default OrderDetailsSection;
